import { Link } from "@inertiajs/react"
import { useTheme } from "next-themes"
import {
  ChevronsUpDown,
  KeyRound,
  LogOut,
  Moon,
  Settings,
  Sun,
  UserPen,
} from "lucide-react"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { t } from "@/lib/i18n"

export type StaffMenuUser = {
  name: string
  email: string
  initials: string
  avatar: string | null
}

/**
 * The account entry point used to be a bare `<Link>` to /profile in the navbar,
 * with no visible label and an `sr-only` name. Deleting the navbar was the moment
 * to give it the shape `admin-layout`'s `UserMenu` already had: a dropdown whose
 * label carries the name and the email, and whose items are the four things a
 * signed-in person actually does with their own account.
 *
 * The theme toggle rides along because it has nowhere else to be. It was a navbar
 * icon; a theme is a preference of the account, not a destination, so it belongs
 * in the account menu rather than in the navigation list above it.
 */
export function StaffUserMenu({ user }: { user: StaffMenuUser }) {
  const { isMobile, state } = useSidebar()
  const { resolvedTheme, setTheme } = useTheme()
  const dark = resolvedTheme === "dark"
  const collapsed = state === "collapsed" && !isMobile

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              data-testid="staff-account-menu"
              aria-label={t("ui.navigation.account_menu")}
              tooltip={collapsed ? user.name : undefined}
            >
              <Avatar className="size-8 shrink-0">
                <AvatarImage src={user.avatar || ""} alt={user.name} />
                <AvatarFallback className="text-xs">{user.initials}</AvatarFallback>
              </Avatar>
              <span className="grid min-w-0 flex-1 text-left leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate text-sm font-medium">{user.name}</span>
                <span className="truncate text-xs font-normal text-muted-foreground">
                  {user.email}
                </span>
              </span>
              <ChevronsUpDown className="ml-auto size-4 shrink-0 opacity-50 group-data-[collapsible=icon]:hidden" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side={isMobile ? "top" : "right"}
            align="end"
            sideOffset={8}
            className="w-56"
          >
            <DropdownMenuLabel className="flex flex-col gap-0.5">
              <span className="truncate text-sm font-medium">{user.name}</span>
              <span className="truncate text-xs font-normal text-muted-foreground">
                {user.email}
              </span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link href="/profile/edit">
                <UserPen />
                {t("ui.react.components.features.profiles.profile_sidebar.edit_profile")}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link href="/profile/password">
                <KeyRound />
                {t("ui.react.components.features.profiles.profile_sidebar.change_password")}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link href="/profile/settings">
                <Settings />
                {t("ui.react.components.features.profiles.profile_sidebar.configuration")}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid="staff-theme-toggle"
              onSelect={(event) => {
                // Keeping the menu open would leave the item reading the theme it
                // just left; closing it is also what every other item here does.
                event.preventDefault()
                setTheme(dark ? "light" : "dark")
              }}
            >
              {dark ? <Sun /> : <Moon />}
              {dark ? t("ui.navigation.light_theme") : t("ui.navigation.dark_theme")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild variant="destructive">
              <Link href="/session" method="delete" as="button" className="w-full">
                <LogOut />
                {t("ui.react.components.features.profiles.profile_sidebar.close_session")}
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
